import { Accordion, AccordionDetails, AccordionSummary, Container, Dialog, DialogContent, DialogTitle, Grid, Hidden, IconButton } from '@material-ui/core';
import asbvApi, { IndexInfo } from 'api/query/asbv';
import BreedingServiceLogo from 'components/breeding-service-logo';
import { ChevronDownOutlinedIcon } from 'components/icons/chevron-down-outlined';
import { InfoOutlinedIcon } from 'components/icons/info-outlined';
import LoadContainer from 'components/load-container';
import ModalCloseButton from 'components/modal-close-button';
import { PercentileHighlight } from 'components/percentile-tag';
import MiniTable from 'components/table/mini-table';
import React, { useEffect, useState } from 'react';
import { useBreakpoints } from 'utils/use-breakpoints';
import { AllASBVsProps } from './all-asbvs-props';
import './all-asbvs.scss';

export const AllASBVs: React.FC<AllASBVsProps> = (props) => {
  const {
    indexes,
    db,
    subIndex,
  } = props;

  const [indexInfo, setIndexInfo] = useState<IndexInfo[]>();
  const [loading, setLoading] = useState<boolean>(true);
  // Store the "selected" index to show its details in a modal on phone.
  const [selectedIndex, setSelectedIndex] = useState<IndexInfo>();
  const { phone, tablet, desktop } = useBreakpoints();

  /**
   * Gets the grid size for the index details.
   *
   * 6 columns for 1 or 2 items, otherwise fit to content.
   */
  const getIndexDetailGridSize = (itemCount: number): 6 | 3 => {
    switch (itemCount) {
      case 1:
      case 2:
        return 6;
      default:
        return 3;
    }
  };

  /**
   * Title for an index detail column.
   * @param param0
   */
  const IndexDetailTitle = ({ title }: { title: string }) => (
    <div className="index-detail__title">{title}</div>
  );

  /**
   * Description for an index detail column.
   * @param param0
   */
  const IndexDetailDescription = ({ description }: { description: string }) => (
    <div className="index-detail__description" dangerouslySetInnerHTML={{ __html: description }} />
  );

  /**
   * Index detail column.
   * @param param0
   */
  const IndexDetail = ({ info }: { info: IndexInfo }) => (
    <>
      {phone && (
        <>
          <IndexDetailTitle title={info.title} />
          <IconButton
            className="sg"
            size="small"
            aria-label="info"
            onClick={() => setSelectedIndex(info)}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </>
      )}
      {tablet && (
        <>
          <Accordion elevation={0} square>
            <AccordionSummary expandIcon={<ChevronDownOutlinedIcon />}>
              <IndexDetailTitle title={info.title} />
            </AccordionSummary>
            <AccordionDetails>
              <IndexDetailDescription description={info.description} />
            </AccordionDetails>
          </Accordion>
        </>
      )}
      {desktop && (
        <>
          <IndexDetailTitle title={info.title} />
          <IndexDetailDescription description={info.description} />
        </>
      )}
    </>
  );

  /**
   * Gets the index info.
   */
  useEffect(() => {
    const getIndexInfo = async () => {
      try {
        const response = await asbvApi.getIndexInfo(db, subIndex);
        setIndexInfo(response);
      } finally {
        setLoading(false);
      }
    };

    getIndexInfo();
  }, [db, subIndex]);

  /**
   * Close the index modal if the screensize changes.
   */
  useEffect(() => {
    if ((tablet || desktop) && selectedIndex) {
      setSelectedIndex(undefined);
    }
  }, [tablet, desktop, selectedIndex]);

  return (
    <div className="sg-all-asbvs">
      <Container>
        <h2>All ASBVs</h2>
      </Container>
      <LoadContainer
        loading={loading}
      >
        {indexInfo && (
          <div className="index-container">
            <Container>
              <Grid className="section" container spacing={4}>
                <Grid className="section__header" item xs={12} sm={5} md={3}>
                  <div className="selection-indexes">
                    <BreedingServiceLogo className="selection-indexes__logo" db={db} />
                    <h3 className="selection-indexes__header">Selection indexes</h3>
                  </div>
                </Grid>
                <Hidden xsDown>
                  <Grid item sm={1} md={1} />
                </Hidden>
                <Grid className="section__description" item xs={12} sm={6} md={6}>
                  <div className="index-description">A selection index combines ASBVs for several traits to give a single value. This reflects the performance of the sheep relative to the breeding objective of the index.</div>
                </Grid>
                <Grid className="section__table" item xs={12} sm={5} md={3}>
                  <MiniTable
                    headerLabels={['', 'Trait', 'ACC.', 'PCTL.']}
                    traits={indexes}
                    percentileHighlights={{ top: PercentileHighlight.Top20, bottom: PercentileHighlight.BottomNone }}
                    showTraitColumn
                  />
                </Grid>
                <Hidden xsDown>
                  <Grid item sm={1} md={1} />
                </Hidden>
                <Grid className="section__detail" item xs={12} sm={6} md={8}>
                  <Grid className="index-detail-container" container spacing={phone ? 6 : 3}>
                    {indexInfo.map((i) => (
                      <Grid
                        className="index-detail"
                        key={i.title}
                        item
                        xs
                        md={getIndexDetailGridSize(indexInfo.length)}
                      >
                        <IndexDetail info={i} />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </div>
        )}
      </LoadContainer>
      <Dialog open={!!selectedIndex} fullScreen>
        <DialogTitle>
          <h5>{selectedIndex?.title}</h5>
        </DialogTitle>
        <ModalCloseButton fullScreen onClose={() => setSelectedIndex(undefined)} />
        <DialogContent>
          {selectedIndex?.description}
        </DialogContent>
      </Dialog>
    </div>
  );
};
